/**
 * AnnonceBlogCTA — Reusable CTA blocks pushing to /analyse-annonce-bateau.
 *
 * Two variants:
 *   - variant="mid"  : compact banner injected mid-article
 *   - variant="end"  : full-width final CTA with mini form
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Anchor, ArrowRight, Link2 } from 'lucide-react';
import { Button } from './ui/button';

export function AnnonceBlogCTA({ variant = 'mid' }) {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');

  const goToAnalyse = () => {
    const clean = url.trim();
    navigate(clean ? `/analyse-annonce-bateau?url=${encodeURIComponent(clean)}#formulaire` : '/analyse-annonce-bateau');
  };

  if (variant === 'mid') {
    return (
      <aside
        className="my-10 md:my-12 not-prose bg-gradient-to-br from-[#3f4816] to-[#302f2c] border-2 border-[#d9fb06]/40 rounded-2xl p-5 md:p-6 shadow-lg"
        data-testid="blog-cta-mid"
      >
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#d9fb06]/20 flex items-center justify-center">
            <Anchor className="text-[#d9fb06]" size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg md:text-xl font-black text-[#d9fb06] uppercase mb-1">
              Vous avez déjà repéré un bateau&nbsp;?
            </h3>
            <p className="text-[#dfddd6] text-sm leading-relaxed">
              Envoyez-moi le lien de l&apos;annonce. Je vous aiderai à identifier les incohérences, les points
              de vigilance et les questions à poser avant de vous déplacer.
            </p>
          </div>
          <Button
            onClick={goToAnalyse}
            data-testid="blog-cta-mid-btn"
            className="flex-shrink-0 bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-6 py-2.5 font-bold uppercase text-xs whitespace-nowrap"
          >
            Faire analyser l&apos;annonce
            <ArrowRight className="ml-1.5" size={14} />
          </Button>
        </div>
      </aside>
    );
  }

  // variant === 'end'
  return (
    <aside
      className="mt-16 not-prose bg-[#1a1c1b] border-2 border-[#d9fb06] rounded-2xl p-6 md:p-10"
      data-testid="blog-cta-end"
    >
      <div className="text-center mb-6">
        <p className="text-[#d9fb06] font-bold uppercase text-xs tracking-widest mb-2">Prêt(e) à passer à l&apos;action&nbsp;?</p>
        <h3 className="text-2xl md:text-3xl font-black text-[#dfddd6] uppercase mb-2">
          Faites analyser votre annonce
        </h3>
        <p className="text-[#888680] text-sm md:text-base">
          Réponse sous 48h ouvrées, sans engagement. Une analyse indépendante avant de vous déplacer.
        </p>
      </div>
      <div className="flex flex-col md:flex-row gap-3 max-w-2xl mx-auto">
        <div className="flex-1 flex items-center gap-2 bg-[#302f2c] border border-[#3f4816] rounded-full px-4 py-3 focus-within:border-[#d9fb06]">
          <Link2 className="text-[#888680] flex-shrink-0" size={18} />
          <input
            type="url"
            inputMode="url"
            data-testid="blog-cta-end-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && goToAnalyse()}
            placeholder="Collez le lien de l'annonce"
            className="flex-1 bg-transparent border-0 outline-none text-[#dfddd6] placeholder-[#666] text-sm min-w-0"
          />
        </div>
        <Button
          onClick={goToAnalyse}
          data-testid="blog-cta-end-btn"
          className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-6 py-3 font-bold uppercase text-sm whitespace-nowrap"
        >
          Demander une analyse
          <ArrowRight className="ml-2" size={16} />
        </Button>
      </div>
    </aside>
  );
}

export default AnnonceBlogCTA;
